// ConversationParts — the ONE conversation look, drawn by both views. v0.290.0
// ---------------------------------------------------------------------------
// Brett (2026-10-31), of the client's report beside the app: "you still have
// the conversation like this. Why isnt it like the app?" The client's page had
// its own copy of the conversation (right-hand "You" bubbles, bigger photos,
// a different reply box), and it never followed the app's redesign. Both
// views now draw these parts, so a change here is a change in both:
//
//   DefectConversation   the office: the item card in the app
//   PublicReportScreen   the client's report link (ReplyThread)
//
// What lives here is the LOOK only: the heading, the chat tabs, the message
// box, a message, its words and photo, the reply box. What each view knows
// and does stays with it - loading, live, sending, seen marks, and the parts
// only one side has (the office's Pause, Remove, "Seen by", "Held"; the
// client's name box and Add photos), which come in through slots.
//
// One message, every view:
//   someone outside the office  a card with a brand edge, "Reply from <name>"
//                               or "Question from <name>"; the reader's own
//                               is the same card, labelled "You"
//   the office                  a plain row, "Reply · <name>", "Signed off",
//                               "Rectified" or "Note"
// Every message sits on the left, badge first. Nothing goes on the right.

import React from 'react';
import { fmtDate } from '../api';
import { X, ImagePlus, Flag } from 'lucide-react';
import { theme, input, btnPrimary, btnSecondary } from '../theme';
import { LiveBadge, ChatAvatar, PresenceStrip } from './LiveChat';

// v0.283.0 - one chat with the client, one with the subcontractors. A message
// with no chat on it is the client's (everything before the split was).
export const CHAT_TABS = [['client', 'Client'], ['trade', 'Subcontractors']];
export function chatOf(c) { return c && c.chat === 'trade' ? 'trade' : 'client'; }

// Written by someone outside the office: the report's recipient, or a person
// answering through a share link.
export function isOutsider(c) {
  return !!c && (c.authorRole === 'guest' || c.authorRole === 'recipient');
}

// The words at the top of a message. `mine` = the reader wrote it.
export function messageLabel(c, mine = false) {
  if (mine) return 'You';
  const name = (c && c.authorName) || '';
  if (c.authorRole === 'guest') return `Reply from ${name || 'the recipient'}`;
  if (c.authorRole === 'recipient') return `Question from ${name || 'the recipient'}`;
  const kind = c.authorRole === 'author' ? 'Reply'
    : c.authorRole === 'owner' ? 'Signed off'
    : c.authorRole === 'rectifier' ? 'Rectified'
    : 'Note';
  return name ? `${kind} · ${name}` : kind;
}

// v0.276.0 - the badge: the company's logo on the office's messages, the
// writer's initials on a person's (they have no logo here). No name, a "?".
export function MessageAvatar({ c, brand = null, mine = false }) {
  if (isOutsider(c)) return <ChatAvatar name={c.authorName || (mine ? 'You' : '')} />;
  if (c.authorRole === 'rectifier') return <ChatAvatar name={c.authorName || 'Rectified'} />;
  const b = brand || {};
  return <ChatAvatar logoUrl={b.logoUrl} name={b.name || c.authorName || 'Office'} />;
}

// The heading: CONVERSATION and who is on the item on the left; Live and the
// view's own action (the office's Pause) on the right. v0.284.0 - a full strip
// beside Live and Pause is wider than a 380 px column, so the right-hand pair
// drops to a second line, still on the right, rather than run off the edge.
export function ConversationLabel({ children, live = null, onLiveRetry = null, action = null, people = null, logoUrl = null, style = null }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
      gap: 8, rowGap: 4, minHeight: 24, ...style,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <div style={{
          fontSize: 11, fontWeight: 600, color: theme.textLight,
          textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>{children}</div>
        <PresenceStrip people={people} logoUrl={logoUrl} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, marginLeft: 'auto' }}>
        <LiveBadge state={live} onRetry={onLiveRetry} />
        {action}
      </div>
    </div>
  );
}

// One tab per chat, each with its count. `kept` marks a chat holding words
// written and not sent (the office keeps a draft per chat). v0.313.0 - the
// office has a tab per subcontractor, so the tabs wrap onto a second line
// rather than run off a phone, and a long name is cut with its whole name on
// hover ([key, label, hover]).
// `flagged` (v0.321.0): { [key]: true } puts a red dot on a tab holding a
// message flagged "response required" (v0.322.0: on a both-chats link too).
export function ChatTabs({ tabs = CHAT_TABS, current, counts = {}, kept = null, flagged = null, onPick, style = null }) {
  return (
    <div role="tablist" aria-label="Who this conversation is with" style={{
      display: 'flex', flexWrap: 'wrap', columnGap: 16, rowGap: 2, borderBottom: `1px solid ${theme.border}`, ...style,
    }}>
      {tabs.map(([key, label, hover]) => {
        const on = current === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={on}
            data-chat={key}
            title={hover || undefined}
            onClick={() => { if (!on && onPick) onPick(key); }}
            style={{
              background: 'none', border: 'none', fontFamily: 'inherit',
              borderBottom: `2px solid ${on ? theme.brand : 'transparent'}`,
              marginBottom: -1, padding: '4px 2px 6px',
              display: 'inline-flex', alignItems: 'baseline', minWidth: 0, maxWidth: '100%', whiteSpace: 'nowrap',
              fontSize: 12, fontWeight: on ? 700 : 600,
              color: on ? theme.text : theme.textLight,
              cursor: on ? 'default' : 'pointer',
            }}
          >
            <span data-chat-label style={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
            <span style={{ marginLeft: 5, fontWeight: 400, color: theme.textLight }}>{counts[key] || 0}</span>
            {flagged && flagged[key] && (
              <span data-flag-dot role="img" aria-label="Response required" title="A message here is flagged: response required" style={{ marginLeft: 5, color: theme.danger }}>●</span>
            )}
            {!on && kept && kept[key] && (
              <span data-kept role="img" aria-label="Not sent yet" title="Words or photos here, not sent yet" style={{ marginLeft: 5, color: theme.warn }}>●</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// The box the messages sit in. `fill` = take the column's height and scroll
// (the office's panel); `maxHeight` = scroll past that height; neither = grow
// with the messages (the client's page on a phone, which scrolls as a page).
// v0.334.0 - Brett: "We need to make the chat screen bigger on the phone."
// A capped thread wears ss-thread-capped: on a phone or narrow screen
// (responsive.css) it takes about two thirds of the screen instead of the
// desktop cap, so the reply box and Send still fit under it.
export const ThreadBox = React.forwardRef(function ThreadBox({ fill = false, maxHeight = null, style = null, className = '', children, ...rest }, ref) {
  const scrolls = fill || maxHeight != null;
  const cls = [className, maxHeight != null ? 'ss-thread-capped' : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div ref={ref} role="log" aria-label="Messages" {...rest} className={cls} style={{
      display: 'flex', flexDirection: 'column', gap: 8,
      padding: '8px 10px', boxSizing: 'border-box',
      background: theme.bg,
      border: `1px solid ${theme.border}`,
      borderRadius: 8,
      ...(fill ? { flex: 1, minHeight: 0 } : null),
      ...(maxHeight != null ? { maxHeight } : null),
      ...(scrolls ? { overflowY: 'auto' } : null),
      ...style,
    }}>{children}</div>
  );
});

// One message. `extra` follows the date on the top line (the office's "Link
// turned off", "Held - not sent yet"); `action` sits at its end (the office's
// remove menu). The words, photo and anything under them are the children.
// `flagged` (v0.321.0): the message is flagged "response required" - a red
// edge, and an office row gets the same padded card. v0.322.0 - the client's
// and subbie's page draws it too, for the flags in their own chat; Brett:
// "thats too much, remove it. just the redbox is fine" (no banner either side).
export function MessageRow({ id, outsider = false, removed = false, flagged = false, avatar = null, label, date = null, extra = null, action = null, children }) {
  const card = outsider || flagged;
  const edge = flagged ? theme.danger : removed ? theme.border : theme.brand;
  return (
    <div data-msg={id} data-from={outsider ? 'outside' : 'office'} data-flagged={flagged ? '1' : undefined} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      {avatar}
      <div style={card ? {
        flex: 1, minWidth: 0,
        padding: '7px 10px',
        background: theme.surface,
        border: `1px solid ${flagged ? theme.danger : theme.border}`,
        borderLeft: `3px solid ${edge}`,
        borderRadius: 6,
        fontSize: 12,
      } : { flex: 1, minWidth: 0, fontSize: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 2 }}>
          <div data-msg-label style={{
            flex: 1, minWidth: 0, fontSize: 11,
            fontWeight: outsider ? 700 : 600,
            color: outsider && !removed ? theme.brand : theme.textLight,
          }}>
            {label}
            {date && <time dateTime={String(date)} style={{ color: theme.textLight, fontWeight: 400, marginLeft: 6 }}>{fmtDate(date)}</time>}
            {extra}
          </div>
          {action}
        </div>
        {children}
      </div>
    </div>
  );
}

// v0.322.0 - the words beside a flagged message's date. `title` says who
// flagged it and when, on hover (each view words its own).
export function FlagTag({ title }) {
  return (
    <span data-flag-tag title={title} style={{
      color: theme.danger, fontWeight: 700, marginLeft: 6, whiteSpace: 'nowrap',
      display: 'inline-flex', alignItems: 'center', gap: 3, verticalAlign: 'top',
    }}><Flag size={11} strokeWidth={2.4} aria-hidden="true" />Response required</span>
  );
}

// v0.322.0 - the ⋯ at the end of a message's top line (a MessageRow `action`),
// one look for the office's menu and the client's. `rest` carries each view's
// data- marks.
export function MessageMenuButton({ open = false, title = 'Message options', onClick, ...rest }) {
  return (
    <button
      type="button"
      {...rest}
      aria-label="Message options"
      aria-expanded={open}
      title={title}
      onClick={onClick}
      style={{
        flexShrink: 0, marginLeft: 6, padding: '0 5px',
        background: open ? theme.bg : 'none',
        border: `1px solid ${open ? theme.border : 'transparent'}`,
        borderRadius: 4, color: theme.textLight, fontSize: 13, lineHeight: '16px',
        fontFamily: 'inherit', cursor: 'pointer',
      }}
    >⋯</button>
  );
}

// A message's words. 'struck' = removed, as the office keeps it; 'removed' =
// the "Removed by <company>" a report link reads in its place.
export function MessageText({ tone = 'plain', children, ...rest }) {
  return (
    <div {...rest} style={{
      color: tone === 'plain' ? theme.text : theme.textLight,
      whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
      ...(tone === 'struck' ? { textDecoration: 'line-through' } : null),
      ...(tone === 'removed' ? { fontStyle: 'italic' } : null),
    }}>{children}</div>
  );
}

// A photo sent with a message: a small square that opens it full size.
export function MessagePhoto({ photo, onOpen, dim = false }) {
  const src = photo && (photo.thumbnailUrl || photo.displayUrl || photo.url);
  if (!src) return null;
  return (
    <button
      type="button"
      title="Open the photo"
      onClick={onOpen}
      style={{
        display: 'block', marginTop: 6, width: 60, height: 60, padding: 0,
        border: `1px solid ${theme.border}`, borderRadius: 6, overflow: 'hidden',
        background: 'transparent', cursor: 'zoom-in',
      }}
    >
      <img
        src={src}
        alt={photo.caption || 'Attached photo'}
        loading="lazy"
        decoding="async"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: dim ? 0.5 : 1 }}
      />
    </button>
  );
}

// v0.320.0 - several photos sent together, as one message: the same squares
// side by side, wrapping. Brett (2026-11-04): "They should be grouped."
// Each tile may carry a small corner mark (the office's add / on-item tick).
export function MessagePhotos({ items, dim = false }) {
  const tiles = (items || []).filter((t) => t && t.photo && (t.photo.thumbnailUrl || t.photo.displayUrl || t.photo.url));
  if (!tiles.length) return null;
  return (
    <div data-part="message-photos" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, maxWidth: 4 * 60 + 3 * 4 }}>
      {tiles.map((t) => (
        <div key={t.key} data-tile={t.key} style={{ position: 'relative', width: 60, height: 60 }}>
          <button
            type="button"
            title="Open the photo"
            onClick={t.onOpen}
            style={{
              display: 'block', width: 60, height: 60, padding: 0,
              border: `1px solid ${theme.border}`, borderRadius: 6, overflow: 'hidden',
              background: 'transparent', cursor: 'zoom-in',
            }}
          >
            <img
              src={t.photo.thumbnailUrl || t.photo.displayUrl || t.photo.url}
              alt={t.photo.caption || 'Attached photo'}
              loading="lazy"
              decoding="async"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: dim ? 0.5 : 1 }}
            />
          </button>
          {t.corner && <div style={{ position: 'absolute', top: 3, right: 3 }}>{t.corner}</div>}
        </div>
      ))}
    </div>
  );
}

const SEND_KEYS = typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '') ? '⌘ Enter' : 'Ctrl+Enter';

// The reply box: the words and Send on one row, Ctrl+Enter (⌘ Enter) sends.
// Slots: `above` (the client's name box), `attachments` (photos waiting to go),
// `tools` (Add photos). Never wider than a comfortable line of writing.
// v0.319.0 - onFiles makes the box a drop target for photos, on both sides.
// Brett (2026-11-04): "build it so i can drag photos into chat." A file
// dragged over the box outlines it in the brand colour with "Drop photos to
// add them"; the drop is taken here and never reaches the card underneath
// (the photo sheet would otherwise file it as one of the item's photos).
export function Composer({
  value, onChange, onSend, sending = false, canSend = null, placeholder = '', rows = 2,
  above = null, attachments = null, tools = null, error = '', flash = '', flashTone = 'success',
  onFiles = null, dropLabel = 'Drop photos to add them',
}) {
  const ready = canSend != null ? !!canSend : (!sending && !!String(value || '').trim());
  const [over, setOver] = React.useState(false);
  const depth = React.useRef(0);
  const carriesFiles = (e) => !!(e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files'));
  const drop = onFiles ? {
    // Enter and over go on up, so a card around the box (the photo sheet)
    // can see the file is over the chat and drop its own highlight; only the
    // drop itself stops here.
    onDragEnter: (e) => { if (!carriesFiles(e)) return; e.preventDefault(); depth.current += 1; setOver(true); },
    onDragOver: (e) => { if (!carriesFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; if (!over) setOver(true); },
    onDragLeave: (e) => { if (!carriesFiles(e)) return; depth.current = Math.max(0, depth.current - 1); if (!depth.current) setOver(false); },
    onDrop: (e) => {
      if (!carriesFiles(e)) return;
      e.preventDefault(); e.stopPropagation();
      depth.current = 0; setOver(false);
      const files = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
      if (files.length) onFiles(files);
    },
  } : {};
  return (
    <div
      data-part="composer"
      data-drop={onFiles ? (over ? 'over' : 'idle') : undefined}
      {...drop}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 640, minWidth: 0, position: 'relative',
        ...(onFiles ? {
          borderRadius: 10,
          outline: over ? `2px dashed ${theme.brand}` : '2px dashed transparent',
          outlineOffset: 4,
          background: over ? theme.bg : undefined,
        } : {}),
      }}
    >
      {over && (
        <div data-part="drop-hint" style={{ fontSize: 12, fontWeight: 700, color: theme.brand }}>{dropLabel}</div>
      )}
      {above}
      {attachments}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); if (ready) onSend(); }
          }}
          rows={rows}
          maxLength={2000}
          placeholder={placeholder}
          aria-label={placeholder || 'Message'}
          disabled={sending}
          style={{ ...input, flex: 1, minWidth: 0, minHeight: 44, fontFamily: 'inherit', resize: 'vertical' }}
        />
        <button
          type="button"
          onClick={() => onSend()}
          disabled={!ready}
          title={`Send (${SEND_KEYS})`}
          style={{
            ...btnPrimary,
            flexShrink: 0,
            opacity: ready ? 1 : 0.5,
            cursor: ready ? 'pointer' : 'default',
          }}
        >{sending ? 'Sending…' : 'Send'}</button>
      </div>
      {tools}
      {error && <div role="alert" style={{ fontSize: 11, fontWeight: 600, color: theme.danger }}>{error}</div>}
      {flash && <div role="status" style={{ fontSize: 11, fontWeight: 600, color: flashTone === 'warn' ? theme.warn : theme.success }}>{flash}</div>}
    </div>
  );
}


// v0.319.0 - the photos waiting to go with a message, and the button that
// adds them: one copy for both sides (they were the client page's own until
// the office could send photos too). pics: [{ key, dataUrl }].
export function PhotoAttachments({ pics, onRemove, busy = false }) {
  if (!pics || !pics.length) return null;
  return (
    <div data-part="photo-attachments" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {pics.map(p => (
        <div key={p.key} style={{ position: 'relative', width: 56, height: 56, borderRadius: 6, overflow: 'hidden', border: '1px solid ' + theme.border }}>
          <img src={p.dataUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          {!busy && (
            <button
              type="button"
              onClick={() => onRemove(p.key)}
              title="Remove this photo"
              aria-label="Remove this photo"
              style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, padding: 0, borderRadius: 9, border: 'none', background: 'rgba(15,23,42,0.65)', color: 'white', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            ><X size={12} /></button>
          )}
        </div>
      ))}
    </div>
  );
}

export function AddPhotosButton({ onFiles, max, full = false, note = '' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <label
        data-part="add-photos"
        title={`Add up to ${max} photos — or drag them onto the box`}
        style={{
          ...btnSecondary, fontSize: 12, padding: '5px 10px',
          alignSelf: 'flex-start',
          display: 'inline-flex', alignItems: 'center', gap: 5,
          opacity: full ? 0.5 : 1,
          cursor: full ? 'default' : 'pointer',
        }}
      >
        <ImagePlus size={14} />Add photos
        <input
          type="file"
          accept="image/*"
          multiple
          disabled={full}
          onChange={(e) => { const list = Array.from(e.target.files || []); e.target.value = ''; if (list.length) onFiles(list); }}
          style={{ display: 'none' }}
        />
      </label>
      {note && <span style={{ fontSize: 11, color: theme.textLight }}>{note}</span>}
    </div>
  );
}
